import React, { useMemo, useState, useCallback } from "react";
import { JobItemEntityType, OrganizationKeys, SupabaseRpcEnum } from "@/lib/supabase/supabaseTypes";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TableSortLabel,
	Checkbox,
	Chip,
	Typography,
	Tooltip,
} from "@mui/material";
import { Logger } from "@/lib/logger/Logger";
import { supabase } from "@/lib/supabase";
import { useExportActions } from "../../../hooks/actions/useExportActions";
import {
	generateZakXml,
	type ZakExportData,
} from "../../../lib/utils/zak-xml-generator";
import { PdfTemplateType } from "../../../pdf-templates/document-template.types";
import JSZip from "jszip";
import { useCentralStore } from "../../../store/Central";
import { getCountryFromSwissPostalCode } from "../../../lib/utils/country-utils";
import { v4 as uuidv4 } from "uuid";
import { getAccountingRecipient } from "../utils";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useAccountingStore } from "@/dentlab/src/store/Accounting";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { convertExtendedJobDocumentToJdAndJi } from "@/dentlab/src/lib/utils/job-document-converter";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { prepareDocumentExportData } from "../utils";
import { uploadFileWithProvidedPath, useStorage } from "../../../hooks/useStorage";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { useAutoSave } from "../../../hooks/useAutosave/useAutosave";
import { Eye, EyeOff, Send } from "lucide-react";
import { TableInvoiceRow } from "../types/invoice-table.types";
import { hasConfigValue } from "@/dentlab/src/layout/layout-components/app-bar/DlSideNavigation";
import { formatCurrencyWithDash } from "../../../lib/utils/utils-functions";
import { InvoiceType } from "../../../types/database-enums/invoices-enums";
import { useRemindersStore } from "../../../store/Reminders";

interface AccountingZakDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	accountingData: TableInvoiceRow[];
	onZakSent?: (invoiceIds: number[]) => void;
}

export const AccountingZakDialog: React.FC<AccountingZakDialogProps> = ({
	open,
	onOpenChange,
	accountingData,
	onZakSent,
}) => {
	const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(
		new Set()
	);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [exportProgress, setExportProgress] = useState<number>(0);
	const [zakMessage, setZakMessage] = useState<string>("");
	const [sortBy, setSortBy] = useState<string>('invoice_doc_number');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
	const [hideAlreadySent, setHideAlreadySent] = useState<boolean>(true);

	const { 
		exportRechnung,
		exportSammelrechnung,
		exportBarzahlungsquittung,
		exportKartenzahlungsquittung,
		paymentSlipDataFor
	} = useExportActions();

	const { getExtendedJobDocuments } = useAccountingStore((state) => ({
		getExtendedJobDocuments: state.getExtendedJobDocuments
	}))

	const {
		clientsLookup,
		guarantorLookup,
		updateOrganization,
		bankAccountsLookup
	} = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
		guarantorLookup: state.guarantorLookup,
		updateOrganization: state.updateOrganization,
		bankAccountsLookup: state.bankAccountsLookup
	}));

	const { fetchJob } = useJobStore()

	const { getPatient } = usePatientStore()

	const { downloadBlob } = useStorage()

	const organization = useCentralStore((state) => state.organization);
	
	// ZAK Configuration states
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [zakConfig, setZakConfig] = useState({
		zak_username: organization?.zak_username || "",
		zak_contract_no: organization?.zak_contract_no || "",
		zak_order_number: organization?.zak_order_number || 0,
		zak_password: organization?.zak_password || "",
	});

	// Filter invoices based on selected statuses and already sent filter
	const filteredInvoices = useMemo(() => {
		let filtered = accountingData.filter(
			(invoice) => invoice.invoice_type !== InvoiceType.MAHNGEBUEHR
		);
		
		// Filter out already sent invoices if enabled
		if (hideAlreadySent) {
			filtered = filtered.filter((invoice) => !invoice.is_sent_to_zak);
		}
		
		return filtered;
	}, [accountingData, hideAlreadySent]);

	// Handle individual invoice selection
	const handleInvoiceSelect = (invoiceId: number, checked: boolean) => {
		setSelectedInvoices((prev) => {
			const newSet = new Set(prev);
			if (checked) {
				newSet.add(invoiceId);
			} else {
				newSet.delete(invoiceId);
			}
			return newSet;
		});
	};

	// Sort filtered invoices
	const sortedInvoices = useMemo(() => {
		return [...filteredInvoices].sort((a, b) => {
			let aVal: any, bVal: any;

			const aRecipient = getAccountingRecipient(a) || '';
			const bRecipient = getAccountingRecipient(b) || '';

			switch (sortBy) {
				case 'invoice_doc_number':
					aVal = a.invoice_doc_number || '';
					bVal = b.invoice_doc_number || '';
					break;
				case 'invoice_date':
					aVal = a.invoice_date ? new Date(a.invoice_date) : new Date(0);
					bVal = b.invoice_date ? new Date(b.invoice_date) : new Date(0);
					break;
				case 'invoice_value':
					aVal = a.invoice_value || 0;
					bVal = b.invoice_value || 0;
					break;
				case 'recipient':
					aVal = aRecipient.toLowerCase();
					bVal = bRecipient.toLowerCase();
					break;
				default:
					return 0;
			}

			// Handle comparison based on data type
			if (typeof aVal === 'string' && typeof bVal === 'string') {
				const comparison = aVal.localeCompare(bVal);
				return sortDirection === 'asc' ? comparison : -comparison;
			} else if (typeof aVal === 'number' && typeof bVal === 'number') {
				const comparison = aVal - bVal;
				return sortDirection === 'asc' ? comparison : -comparison;
			} else if (aVal instanceof Date && bVal instanceof Date) {
				const comparison = aVal.getTime() - bVal.getTime();
				return sortDirection === 'asc' ? comparison : -comparison;
			}

			return 0;
		});
	}, [filteredInvoices, sortBy, sortDirection]);

	// Handle select all
	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			const allIds = sortedInvoices
				.map((invoice) => invoice.invoice_id)
				.filter((id): id is number => id !== null);
			setSelectedInvoices(new Set(allIds));
		} else {
			setSelectedInvoices(new Set());
		}
	};

	// Check if all sorted invoices are selected
	const allSelected =
		sortedInvoices.length > 0 &&
		sortedInvoices.every((invoice) =>
			invoice.invoice_id
				? selectedInvoices.has(invoice.invoice_id)
				: false
		);

	// Check if some invoices are selected
	const someSelected = selectedInvoices.size > 0 && !allSelected;

	// Handle column sorting
	const handleSort = (column: string) => {
		if (sortBy === column) {
			// Toggle direction if same column
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			// New column, start with ascending
			setSortBy(column);
			setSortDirection('asc');
		}
	};

	const getRecipient = (invoice: TableInvoiceRow) => {
		const recipientId = invoice.recipient_client_id || invoice.recipient_guarantor_id;
		const recipientLookup = invoice.recipient_client_id ? clientsLookup : guarantorLookup;
		return recipientId ? recipientLookup[recipientId] : null;
	}

	// Memoize the original config to prevent infinite loops
	const originalConfig = useMemo(() => ({
		zak_username: organization?.zak_username || "",
		zak_contract_no: organization?.zak_contract_no || "",
		zak_order_number: organization?.zak_order_number || 0,
		zak_password: organization?.zak_password || "",
	}), [organization?.zak_username, organization?.zak_contract_no, organization?.zak_order_number, organization?.zak_password]);

	// Auto-save handler for ZAK configuration form  
	const saveZakConfig = useCallback(async () => {
		// Only update fields that have changed
		const changedFields = Object.entries(zakConfig).filter(
			([key, value]) => value !== originalConfig[key as keyof typeof originalConfig]
		);

		if (changedFields.length === 0) return;

		try {
			// Update all changed fields
			for (const [fieldName, value] of changedFields) {
				const { error } = await updateOrganization(fieldName as OrganizationKeys, value, true);
				if (error) {
					showNotification({
						message: `Fehler beim Aktualisieren von ${fieldName}`,
						type: "error",
					});
					Logger.error(`Error updating ${fieldName}:`, error);
					return;
				}
			}

		} catch (error) {
			showNotification({
				message: "Fehler beim Aktualisieren der ZAK Konfiguration",
				type: "error",
			});
			Logger.error("Error updating ZAK config:"+ error);
		}
	}, [zakConfig, originalConfig, updateOrganization]);

	const hasChanges = useMemo(() => {
		// Early termination for common case - if objects are the same reference
		if (zakConfig === originalConfig) return false;
		
		// Direct property comparison without Object.keys() and type casting
		return (
			zakConfig.zak_username !== originalConfig.zak_username ||
			zakConfig.zak_contract_no !== originalConfig.zak_contract_no ||
			zakConfig.zak_order_number !== originalConfig.zak_order_number ||
			zakConfig.zak_password !== originalConfig.zak_password
		);
	}, [zakConfig, originalConfig]);

	// Use autosave hook for the entire form
	useAutoSave({
		data: zakConfig,
		onSave: saveZakConfig,
		shouldSave: hasChanges,
		delay: 500,
	});

	// Helper function to update ZAK config fields
	const updateZakConfig = (field: keyof typeof zakConfig, value: string | number) => {
		setZakConfig(prev => ({
			...prev,
			[field]: value
		}));
	};

	// Upload ZIP to ZAK via backend microservice
	const uploadToZAK = async ({
		labId,
		contractNo,
		username,
		password,
		filePath
	}: {
		labId: string;
		contractNo: string;
		username: string;
		password: string;
		filePath: string;
	}): Promise<{ success: boolean; error?: string }> => {
		try {
			// Create base64 encoded credentials for backend service
			const credentials = btoa(`${username}:${password}`);

			// Send the data to the backend, due to a CORS error
			const url = `${import.meta.env.VITE_PDF_MICROSERVICE_URL}/zak/upload`;

			Logger.log(`Uploading to ZAK via backend: ${url}`);
			Logger.log(`Contract No: ${contractNo}, Lab ID: ${labId}, File path: ${filePath}`);

			// HTTP POST request to backend with 180 second timeout
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					contract_no: contractNo,
					credentials: credentials,
					zak_lab_id: labId,
					file_path: filePath
				}),
				// 180 second timeout for large files
				signal: AbortSignal.timeout(180000)
			});

			// Parse JSON response from backend
			const result = await response.json().catch(() => null);

			// Log response details
			Logger.log(`Backend HTTP Response: ${response.status} ${response.statusText}`);
			if (result) {
				Logger.log('Backend response:', result);
			}

			// Check for HTTP errors or backend failure
			if (response.status >= 400 || !result?.success) {
				const errorMessage = result?.error || `HTTP ${response.status}: ${response.statusText}`;
				Logger.error(`ZAK upload failed via backend: ${errorMessage}`);
				return { success: false, error: errorMessage };
			}

			// Success response
			Logger.log(`ZAK upload successful via backend: ${result.message || 'Upload completed'}`);
			return { success: true };

		} catch (error) {
			let errorMessage = 'Unknown error';
			
			if (error instanceof Error) {
				if (error.name === 'AbortError') {
					errorMessage = 'Zeitüberschreitung (180s)';
				} else if (error.message.includes('Failed to fetch')) {
					errorMessage = 'Backend-Service nicht erreichbar';
				} else if (error.message.includes('NetworkError')) {
					errorMessage = 'Netzwerkfehler beim Backend-Aufruf';
				} else {
					errorMessage = error.message;
				}
			}

			Logger.error('ZAK upload error via backend:'+ error);
			return { success: false, error: errorMessage };
		}
	};

	// Update is_sent_to_zak status for processed invoices
	const updateInvoicesZakStatus = async (invoiceIds: number[]): Promise<{ success: boolean; error?: string }> => {
		try {
			Logger.log(`Updating is_sent_to_zak status for ${invoiceIds.length} invoices:`, invoiceIds);

			const { error } = await supabase
				.from('invoices')
				.update({ is_sent_to_zak: true })
				.in('id', invoiceIds);

			if (error) {
				Logger.error('Error updating invoice ZAK status:' + error);
				return { success: false, error: error.message };
			}

			Logger.log(`Successfully updated is_sent_to_zak status for invoices: ${invoiceIds.join(', ')}`);
			return { success: true };

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			Logger.error('Database update error:' + error);
			return { success: false, error: errorMessage };
		}
	};

	const handleSubmit = async () => {
		if (selectedInvoices.size === 0) return;
		if (!organization) {
			Logger.error("Organization not found in global state");
			return;
		}

		setIsExporting(true);
		setExportProgress(0);

		try {
			const invoiceIds = Array.from(selectedInvoices);
			Logger.log("Selected invoices:", invoiceIds);

			// Get ZAK credentials and order number from organization global state
			const { zak_username: zakUsername, zak_password: zakPassword, zak_order_number: zakOrderNumber, zak_contract_no: zakContractNumber } =
				organization;

			if (!zakUsername || !zakPassword) {
				Logger.error(
					"ZAK credentials not configured for this organization"
				);
				return;
			}

			const currentOrderNumber = zakOrderNumber || 1;
			const prefix = `${zakContractNumber}_${currentOrderNumber}`

			// Get selected invoice data from accountingData
			const selectedInvoiceData = accountingData
				.filter(
					(invoice) =>
						invoice.invoice_id &&
						selectedInvoices.has(invoice.invoice_id)
				)
				.filter((invoice) => invoice.invoice_doc_number); // Filter out invoices with null doc_number

			// Calculate total steps: PDFs + XML + ZIP + Upload
			const totalSteps = selectedInvoiceData.length + 3;
			let currentStep = 0;

			// Helper function to update progress
			const updateProgress = (increment: number = 1) => {
				currentStep += increment;
				setExportProgress(Math.round((currentStep / totalSteps) * 100));
			};

			// Build ZAK export data using available information
			const zakData: ZakExportData = {
				order_no: currentOrderNumber,
				contract_no: String(zakContractNumber),
				order_date: new Date().toLocaleDateString("de-DE"),
				invoice_count: selectedInvoiceData.length,
				total_amount: selectedInvoiceData.reduce(
					(sum, inv) => sum + (inv.invoice_value || 0),
					0
				),
				system: "dentLAB version 2.0",
				comment: zakMessage,
				invoices: await Promise.all(selectedInvoiceData.map(async (invoice) => {

					const recipient = getRecipient(invoice)

					const recipientData = {
						id: recipient?.id || "",
						title: recipient?.is_company ? "" : recipient?.title ?? "",
						firstname: recipient?.is_company ? "" : (recipient?.first_name || ""),
						lastname: recipient?.is_company ? (recipient?.company_name || "") : (recipient?.last_name || ""),
						language: "de", //TODO add language to recipients
						birthday: "",
						phone_no: recipient?.phone_business || "",
						phone_no2: "", //Don't give private number
						email: recipient?.email || "",
						street1: recipient?.street || "",
						zipcode: recipient?.postal_code || "",
						town: recipient?.city || "",
						country: (
							recipient?.country ||
							getCountryFromSwissPostalCode(recipient?.postal_code)
						).toUpperCase(),
					};

					const patient = invoice.patient_id ? await getPatient(invoice.patient_id) : null

					const patientData = {
						id: patient?.id || "",
						title: patient?.title ?? "",
						firstname: patient?.first_name || "",
						lastname: patient?.last_name || "",
						language: "de", //TODO add language to patients
						birthday: patient?.birth_date || "",
						phone_no: "",
						phone_no2: "",
						email: "",
						street1: patient?.street || "",
						zipcode: patient?.postal_code || "",
						town: patient?.city || "",
						country: (
							patient?.country ||
							getCountryFromSwissPostalCode(patient?.postal_code)
						).toUpperCase(),
					};

					return {
						invoice_no: invoice.invoice_doc_number!,
						invoice_file: `${prefix}_${invoice.invoice_doc_number}.pdf`,
						invoice_date: invoice.invoice_date
							? new Date(invoice.invoice_date).toLocaleDateString("de-DE")
							: new Date().toLocaleDateString("de-DE"),
						treatment_from: invoice.invoice_date
							? new Date(invoice.invoice_date).toLocaleDateString("de-DE")
							: new Date().toLocaleDateString("de-DE"),
						treatment_until: invoice.invoice_date
							? new Date(invoice.invoice_date).toLocaleDateString("de-DE")
							: new Date().toLocaleDateString("de-DE"),
						invoice_amount: invoice.invoice_value || 0,
						lab_amount: 0, //TODO
						down_payment: 0, //TODO
						recipient: recipientData,
						patient: patient ? patientData : recipientData,
					};
				})),
				organization,
				zak_credentials: {
					username: zakUsername,
					password: zakPassword,
				},
			};

			// Create ZIP file
			const zip = new JSZip();

			// Generate and validate XML file
			const xmlContent = generateZakXml(zakData);
			
			// Validate XML content
			if (!xmlContent || typeof xmlContent !== 'string') {
				showNotification({
					message: "XML-Generierung fehlgeschlagen - Ungültiger Inhalt",
					type: "error"
				});
				Logger.error("XML generation returned invalid content");
				return;
			}

			if (xmlContent.trim().length === 0) {
				showNotification({
					message: "XML-Generierung fehlgeschlagen - Leerer Inhalt",
					type: "error"
				});
				Logger.error("XML generation returned empty content");
				return;
			}

			// Basic XML structure validation
			if (!xmlContent.includes('<order') || !xmlContent.includes('</order>')) {
				showNotification({
					message: "XML-Generierung fehlgeschlagen - Ungültige XML-Struktur",
					type: "error"
				});
				Logger.error("XML generation returned malformed XML structure");
				return;
			}

			zip.file(`${prefix}.xml`, xmlContent);
			Logger.log(`Successfully generated XML file (${Math.round(xmlContent.length / 1024)}KB)`);

			// Update progress after XML generation
			updateProgress();

			// Generate PDFs for each invoice individually with validation
			const generatedPdfs: { fileName: string; blob: Blob }[] = [];
			for (const invoiceData of selectedInvoiceData) {
				try {
					const pdfBlob = await generateIndividualInvoicePdf(invoiceData);

					// Validate PDF generation
					if (!pdfBlob) {
						showNotification({
							message: `PDF-Generierung für Rechnung ${invoiceData.invoice_doc_number} fehlgeschlagen`,
							type: "error"
						});
						Logger.error(`PDF generation returned null for invoice ${invoiceData.invoice_doc_number}`);
						return;
					}

					// Validate PDF blob size
					if (pdfBlob.size === 0) {
						showNotification({
							message: `PDF für Rechnung ${invoiceData.invoice_doc_number} ist leer`,
							type: "error"
						});
						Logger.error(`PDF generation returned empty blob for invoice ${invoiceData.invoice_doc_number}`);
						return;
					}

					if (pdfBlob.size > 50 * 1024 * 1024) {
						showNotification({
							message: `PDF für Rechnung ${invoiceData.invoice_doc_number} ist zu groß (${Math.round(pdfBlob.size / 1024 / 1024)}MB)`,
							type: "error"
						});
						Logger.error(`PDF too large for invoice ${invoiceData.invoice_doc_number}: ${pdfBlob.size} bytes`);
						return;
					}

					const fileName = `${prefix}_${invoiceData.invoice_doc_number}.pdf`;
					generatedPdfs.push({ fileName, blob: pdfBlob });
					zip.file(fileName, pdfBlob);

					Logger.log(`Successfully generated PDF for invoice ${invoiceData.invoice_doc_number} (${Math.round(pdfBlob.size / 1024)}KB)`);

					// Update progress after each PDF generation
					updateProgress();
				} catch (pdfError) {
					showNotification({
						message: `Fehler bei der PDF-Generierung für Rechnung ${invoiceData.invoice_doc_number}`,
						type: "error"
					});
					Logger.error(`Failed to generate PDF for invoice ${invoiceData.invoice_doc_number}:`);
					Logger.error(pdfError);
					return;
				}
			}

			// Generate ZIP file with integrity validation
			const zipBlob = await zip.generateAsync({ type: "blob" });
			
			// Validate ZIP integrity
			if (!zipBlob) {
				showNotification({
					message: "ZIP-Archiv konnte nicht erstellt werden",
					type: "error"
				});
				Logger.error("ZIP generation returned null");
				return;
			}

			if (zipBlob.size === 0) {
				showNotification({
					message: "ZIP-Archiv ist leer",
					type: "error"
				});
				Logger.error("ZIP generation returned empty blob");
				return;
			}

			Logger.log(`Successfully generated ZIP file (${Math.round(zipBlob.size / 1024)}KB)`);

			// Update progress after ZIP generation
			updateProgress();

			// Verify ZIP content contains all expected files
			const expectedFiles = [
				`${prefix}.xml`,
				...generatedPdfs.map(pdf => pdf.fileName)
			];

			try {
				// Load the ZIP to verify its contents
				const verifyZip = new JSZip();
				const loadedZip = await verifyZip.loadAsync(zipBlob);
				const zipFileNames = Object.keys(loadedZip.files);

				// Check if all expected files are present
				const missingFiles = expectedFiles.filter(expectedFile => 
					!zipFileNames.includes(expectedFile)
				);

				if (missingFiles.length > 0) {
					showNotification({
						message: `ZIP-Archiv ist unvollständig. Fehlende Dateien: ${missingFiles.join(', ')}`,
						type: "error"
					});
					Logger.error(`ZIP content verification failed. Missing files: ${missingFiles.join(', ')}`);
					return;
				}

				// Check if ZIP contains unexpected extra files
				const extraFiles = zipFileNames.filter(zipFile => 
					!expectedFiles.includes(zipFile)
				);

				if (extraFiles.length > 0) {
					Logger.warn(`ZIP contains unexpected files: ${extraFiles.join(', ')}`);
				}

				Logger.log(`ZIP content verification passed. Contains ${zipFileNames.length} files: ${zipFileNames.join(', ')}`);
			} catch (zipVerificationError) {
				showNotification({
					message: "ZIP-Archiv konnte nicht verifiziert werden - möglicherweise beschädigt",
					type: "error"
				});
				Logger.error("ZIP content verification failed:");
				Logger.error(zipVerificationError);
				return;
			}

			const fileName = `${prefix}.zip`;

			// First, upload to Supabase storage (backend will download from here)
			const filePath = `${organization.id}/zak/${uuidv4()}/${fileName}`;

			try {
				// Upload to Supabase Storage
				const storageUploadResult = await uploadFileWithProvidedPath({
					filePath,
					fileBody: zipBlob,
					bucketName: "v1",
				});

				if (!storageUploadResult.success || !storageUploadResult.pathName) {
					Logger.error("Error uploading ZIP file to storage");
					showNotification({
						message: "Fehler beim Hochladen der Datei zu Supabase Storage",
						type: "error",
					});
					return;
				}

				Logger.log(`Successfully uploaded ZIP to Supabase storage: ${storageUploadResult.pathName}`);

				// Insert file record into database
				if (organization.id) {
					const { error: fileInsertError } = await supabase
						.from("files")
						.insert({
							file_name: fileName,
							path_name: storageUploadResult.pathName,
							organization_id: organization.id,
							bucket_name: "v1",
							meta_data: {"type": "zip"}
						});

					if (fileInsertError) {
						Logger.error("Error inserting file record:");
						Logger.error(fileInsertError);
						showNotification({
							message: "Datei hochgeladen, aber Datensatz konnte nicht gespeichert werden",
							type: "warning",
						});
					}
				}

				// Now upload to ZAK server via backend (backend will download from Supabase)
				setIsUploading(true);

				try {
					const uploadResult = await uploadToZAK({
						labId: String(zakContractNumber), // Use contract number as lab identifier
						contractNo: String(zakContractNumber), // Contract number in form data
						username: zakUsername,
						password: zakPassword,
						filePath: storageUploadResult.pathName
					});

					if (uploadResult.success) {
						// Update progress after successful upload
						updateProgress();

						Logger.log("ZAK HTTP upload completed successfully");

						// Update database is_sent_to_zak status for processed invoices
						const invoiceIds = selectedInvoiceData.map(inv => inv.invoice_id).filter(id => id !== null) as number[];
						const dbUpdateResult = await updateInvoicesZakStatus(invoiceIds);

						if (dbUpdateResult.success) {
							// Add invoice IDs to highlighted rows
							onZakSent?.(invoiceIds);

							Logger.log("Invoice ZAK status updated successfully");

							// Check if automatic download is enabled and trigger download
							const zakDownloadArchive = hasConfigValue(organization.config, "zak_download_archive", true);
							if (zakDownloadArchive) {
								try {
									await downloadBlob(zipBlob, fileName, true); //does not use overwrite --> large files lead to an error because the last user interaction was too long ago and therefore the browser does not allow it
									Logger.log(`ZIP archive downloaded: ${fileName}`);
								} catch (downloadError) {
									Logger.error("Failed to download ZIP archive:");
									Logger.error(downloadError);
									showNotification({
										message: "Fehler beim Herunterladen des ZIP-Archivs",
										type: "warning"
									});
								}
							}

							// Automatically mark invoices as paid if enabled
							const zakAutoMarkPaid = hasConfigValue(organization.config, "zak_auto_mark_paid", true);
							if (zakAutoMarkPaid) {
								Logger.log("ZAK auto-mark-paid enabled, creating payments...");

								try {
									const currentDate = new Date().toISOString();

									const { data, error } = await supabase.rpc(SupabaseRpcEnum.AUTO_CREATE_ZAK_PAYMENTS, {
										p_invoice_ids: invoiceIds,
										p_valuta_date: currentDate,
										p_note: "Automatisch erstellt - ZAK Versand"
									});

									if (error) {
										Logger.error("Failed to auto-create ZAK payments:"+ error);
										showNotification({
											message: "Fehler beim automatischen Erstellen der Zahlungen",
											type: "error"
										});
									} else {
										useRemindersStore.getState().fetchDueCount();
										const result = data as any;

										if (result.success_count > 0) {
											showNotification({
												message: `${result.success_count} Zahlung${result.success_count > 1 ? 'en' : ''} automatisch erstellt`,
												type: "success"
											});
										}

										if (result.error_count > 0) {
											showNotification({
												message: `${result.error_count} Zahlung${result.error_count > 1 ? 'en' : ''} konnte${result.error_count === 1 ? '' : 'n'} nicht erstellt werden`,
												type: "warning"
											});
											Logger.error("Payment creation errors:", result.errors);
										}
									}
								} catch (paymentError) {
									Logger.error("Exception during auto-create ZAK payments:"+ paymentError);
									showNotification({
										message: "Fehler beim automatischen Erstellen der Zahlungen",
										type: "error"
									});
								}
							}
						} else {
							showNotification({
								message: `Zahlungen wurden nicht korrekt als versendet markiert.`,
								type: "warning"
							});
							Logger.error(`Database update failed: ${dbUpdateResult.error}`);
						}
					} else {
						showNotification({
							message: `ZAK Upload fehlgeschlagen: ${uploadResult.error}`,
							type: "error"
						});
						Logger.error(`ZAK HTTP upload failed: ${uploadResult.error}`);
						return;
					}
				} catch (uploadError) {
					showNotification({
						message: `ZAK Upload fehlgeschlagen: ${uploadError}`,
						type: "error"
					});
					Logger.error("ZAK HTTP upload error:"+ uploadError);
					return;
				} finally {
					setIsUploading(false);
				}

			} catch (uploadError) {
				Logger.error("Error during storage upload:");
				Logger.error(uploadError);
				showNotification({
					message: "Fehler beim Hochladen der Datei zu Supabase Storage",
					type: "error",
				});
				return;
			}

			updateZakConfig('zak_order_number', currentOrderNumber + 1)

			Logger.log("ZAK export completed successfully");
		} catch (error) {
			Logger.error("ZAK export error:");
			Logger.error(error);
		} finally {
			setIsExporting(false);
			setExportProgress(0);
			closeDialog()
		}
	};

	// Generate PDF for individual invoice by fetching data as needed
	const generateIndividualInvoicePdf = async (
		invoice: TableInvoiceRow
	): Promise<Blob | undefined> => {
		try {
			if (!organization) {
				Logger.error("Organization not found");
				return undefined;
			}

			const type = (invoice.meta_data && typeof invoice.meta_data === 'object' && "pdf_template_type" in invoice.meta_data) ? invoice.meta_data.pdf_template_type : null

			const recipient = getRecipient(invoice) 

			if(!recipient) return undefined;

			const {data, error} = await supabase.from('job_documents_invoices_relationships').select('job_document_id').eq('invoice_id',invoice.invoice_id ?? 0)

			if(!data || error) {
				showNotification({
					message: "Keine Dokumente gefunden",
					type: "error"
				})
				return undefined;
			}

			const jobDocumentIds = data.map((value) => value.job_document_id)

			const extendedJobDocuments = await getExtendedJobDocuments(jobDocumentIds)

			if(!extendedJobDocuments){
				showNotification({
					message: "Herunterladen der Dokumente fehlgeschlagen",
					type: "error"
				})
				return undefined;
			}

			if(type === PdfTemplateType.SAMMELRECHNUNG){
				const result = await exportSammelrechnung({
					recipient,
					extendedJobDocuments,
					invoiceNumber: invoice.invoice_doc_number ?? "",
					invoiceDate: new Date(invoice.invoice_date ?? ""),
					noPaymentSlip: true,
					akontoAmount: invoice.total_allocated_amount ?? 0
				})
				return result.blob ?? undefined;
			}

			const { jobDocument, jobItems} = convertExtendedJobDocumentToJdAndJi(extendedJobDocuments[0])

			const client = clientsLookup[invoice.client_id ?? ""]
			const job = await fetchJob(jobDocument.job_id)

			if(!client || !job) return;

			// Prepare export data using the utility function
			const prepareResult = await prepareDocumentExportData({
				job,
				client,
				organization,
				jobDocument,
				jobItems: jobItems as JobItemEntityType[],
				templateType: type as PdfTemplateType,
				bankAccountsLookup,
				akontoAmount: invoice.total_allocated_amount ?? 0,
			});

			if (!prepareResult.success) {
				showNotification({
					message: prepareResult.error || "Fehler bei der Vorbereitung der Exportdaten",
					type: "error"
				})
				return undefined;
			}

			const { documentProps } = prepareResult.data!;

			// Get payment slip data
			const paymentSlipData = paymentSlipDataFor(documentProps, invoice.invoice_doc_number ?? "");
			if (!paymentSlipData) {
				showNotification({
					message: "Zahlungsinformationen konnten nicht erstellt werden", 
					type: "error"
				})
				return undefined;
			}

			// Call export function directly based on type
			let exportResult = undefined;
			if (type === PdfTemplateType.RECHNUNG) {
				exportResult = await exportRechnung(
					documentProps,
					paymentSlipData,
					documentProps.patient,
					undefined,
					true
				);
			} else if (type === PdfTemplateType.BARZAHLUNGSQUITTUNG) {
				exportResult = await exportBarzahlungsquittung(
					documentProps,
					paymentSlipData
				);
			} else if (type === PdfTemplateType.KARTENZAHLUNGSQUITTUNG) {
				exportResult = await exportKartenzahlungsquittung(
					documentProps,
					paymentSlipData
				);
			} else {
				showNotification({
					message: "Unbekannter PDF-Typ",
					type: "error"
				})
				return undefined;
			}

			if (!exportResult.success) {
				showNotification({
					message: "Erstellen der Rechnung fehlgeschlagen",
					type: "error"
				})
				Logger.log(exportResult.error)
				return undefined;
			}

			return exportResult.blob ?? undefined	
		} catch (error) {
			Logger.error(`Error generating PDF for invoice ${invoice.invoice_doc_number}:`);
			Logger.error(error);
			return undefined;
		}
	};


	const formatDate = (dateString: string | null | undefined) => {
		if (!dateString) return "-";
		return new Date(dateString).toLocaleDateString("de-CH");
	};

	const closeDialog = () => {
		onOpenChange(false);
		setSelectedInvoices(new Set());
		setZakMessage("");
		setShowPassword(false);
		setHideAlreadySent(true);
		setExportProgress(0);
	}
	return (
		<Dialog
			open={open}
			onClose={closeDialog}
			maxWidth="lg"
			fullWidth
		>
			<DialogTitle>An ZAK senden</DialogTitle>
			<DialogContent
				sx={{
					height: "70vh",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Side-by-side layout for Message and Configuration */}
				<div className="grid grid-cols-2 gap-6">
					{/* Left side: ZAK Message */}
					<div>
						<Label htmlFor="zak-message" className="block mb-1 text-sm">
							Meldung an die ZAK:
						</Label>
						<Textarea
							id="zak-message"
							value={zakMessage}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setZakMessage(e.target.value)}
							rows={5}
						/>
						{/* Filter for already sent invoices */}
				<div className="flex items-center mb-4">
					<Checkbox
						checked={hideAlreadySent}
						onChange={(e) => setHideAlreadySent(e.target.checked)}
						inputProps={{
							'aria-label': 'Bereits gesendete Rechnungen ausblenden',
						}}
					/>
					<Label className="cursor-pointer" onClick={() => setHideAlreadySent(!hideAlreadySent)}>
						Bereits gesendete Rechnungen ausblenden
					</Label>
				</div>
					</div>

					{/* Right side: ZAK Configuration in 2x2 grid */}
					<div>
						<div className="grid grid-cols-2 gap-3">
							{/* ZAK Username */}
							<div>
								<Label htmlFor="zak-username" className="block mb-1 text-sm">
									Benutzername
								</Label>
								<Input
									id="zak-username"
									type="text"
									value={zakConfig.zak_username}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										updateZakConfig('zak_username', e.target.value);
									}}
									placeholder="Benutzername"
								/>
							</div>

							{/* ZAK Contract Number */}
							<div>
								<Label htmlFor="zak-contract" className="block mb-1 text-sm">
									Abkommensnummer
								</Label>
								<Input
									id="zak-contract"
									type="number"
									value={zakConfig.zak_contract_no || ""}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const value = e.target.value === "" ? 0 : parseInt(e.target.value) || 0;
										updateZakConfig('zak_contract_no', value);
									}}
									placeholder="Nummer"
								/>
							</div>

							{/* ZAK Order Number */}
							<div>
								<Label htmlFor="zak-order" className="block mb-1 text-sm">
									Auftragsnummer
								</Label>
								<Input
									id="zak-order"
									type="number"
									value={zakConfig.zak_order_number || ""}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const value = e.target.value;
										updateZakConfig('zak_order_number', Number(value));
									}}
									placeholder="Nummer"
								/>
							</div>

							{/* ZAK Password */}
							<div>
								<Label htmlFor="zak-password" className="block mb-1 text-sm">
									Passwort
								</Label>
								<div className="relative">
									<Input
										id="zak-password"
										type={showPassword ? "text" : "password"}
										value={zakConfig.zak_password}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											updateZakConfig('zak_password', e.target.value);
										}}
										placeholder="Passwort"
										className="pr-8"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
									>
										{showPassword ? (
											<EyeOff className="h-3 w-3" />
										) : (
											<Eye className="h-3 w-3" />
										)}
									</button>
								</div>
							</div>
						</div>
						{/* Configuration Info */}
				<div className="mt-3">
					<p className="text-xs text-muted-foreground">
						<strong>Hinweis:</strong> Bei der Auftragsnummer handelt es sich um eine Laufnummer, die bei jeder Übertragung inkrementiert wird. Die angezeigte Laufnummer entspricht dem bevorstehenden Versand.
					</p>
				</div>
					</div>
				</div>

				{/* Invoice Table */}
				<div
					style={{
						flex: 1,
						overflow: "auto",
						border: "1px solid #e0e0e0",
						borderRadius: "4px",
					}}
				>
					{sortedInvoices.length === 0 ? (
						<div style={{ padding: "32px", textAlign: "center" }}>
							<Typography color="text.secondary">
								Keine Rechnungen gefunden
							</Typography>
						</div>
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox">
										<Checkbox
											checked={allSelected}
											indeterminate={someSelected}
											onChange={(e) =>
												handleSelectAll(
													e.target.checked
												)
											}
											inputProps={{
												"aria-label": "Alle auswählen",
											}}
										/>
									</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortBy === 'invoice_doc_number'}
											direction={sortBy === 'invoice_doc_number' ? sortDirection : 'asc'}
											onClick={() => handleSort('invoice_doc_number')}
										>
											Rechnungsnummer
										</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortBy === 'invoice_date'}
											direction={sortBy === 'invoice_date' ? sortDirection : 'asc'}
											onClick={() => handleSort('invoice_date')}
										>
											Datum
										</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortBy === 'invoice_value'}
											direction={sortBy === 'invoice_value' ? sortDirection : 'asc'}
											onClick={() => handleSort('invoice_value')}
										>
											Betrag
										</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortBy === 'recipient'}
											direction={sortBy === 'recipient' ? sortDirection : 'asc'}
											onClick={() => handleSort('recipient')}
										>
											Empfänger
										</TableSortLabel>
									</TableCell>
									{!hideAlreadySent && (
										<TableCell>ZAK Status</TableCell>
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedInvoices.map((invoice) => (
									<TableRow key={invoice.invoice_id}>
										<TableCell padding="checkbox">
											<Checkbox
												checked={
													invoice.invoice_id
														? selectedInvoices.has(
																invoice.invoice_id
															)
														: false
												}
												onChange={(e) => {
													if (invoice.invoice_id) {
														handleInvoiceSelect(
															invoice.invoice_id,
															e.target.checked
														);
													}
												}}
												inputProps={{
													"aria-label": `Rechnung ${invoice.invoice_doc_number} auswählen`,
												}}
											/>
										</TableCell>
										<TableCell
											sx={{ fontWeight: "medium" }}
										>
											{invoice.invoice_doc_number || "-"}
										</TableCell>
										<TableCell>
											{formatDate(invoice.invoice_date)}
										</TableCell>
										<TableCell>
											{formatCurrencyWithDash(
												invoice.invoice_value,
												invoice.invoice_currency
											)}
										</TableCell>
										<TableCell>
											{getAccountingRecipient(invoice)}
										</TableCell>
										{!hideAlreadySent && (
											<TableCell>
												{invoice.is_sent_to_zak && (
													<Tooltip title="An ZAK gesendet">
														<Send className="h-4 w-4 text-green-600" />
													</Tooltip>
												)}
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</DialogContent>
			<DialogActions
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					padding: "16px 24px",
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{selectedInvoices.size} von {sortedInvoices.length}{" "}
					Rechnungen ausgewählt
				</Typography>
				<div style={{ display: "flex", gap: "8px" }}>
					<Button
						variant="outlined"
						onClick={closeDialog}
					>
						Abbrechen
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={selectedInvoices.size === 0 || isExporting || isUploading || hasChanges}
					>
						{isExporting
							? `Senden (${Math.round(exportProgress)}%)`
							: isUploading
							? `Lade hoch (${Math.round(exportProgress)}%)`
							: `Senden (${selectedInvoices.size})`}
					</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
};
